import React from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  Scale,
  ScrollText,
  ArrowRight,
  Library,
  Calculator,
  Info,
  Scroll,
} from 'lucide-react';
import { RESOURCES, SOURCES } from '../../config/learningResources';

/**
 * UnderstandBeforeYouCalculate — compact three-card educational row on the
 * home page, placed directly below the ProductCards (Zakat / Faraid /
 * Wasiyyah intro cards).
 *
 * EDITORIAL POLICY (important)
 * ----------------------------
 * Copy on these cards is deliberately *general educational material*
 * only — the plainest, broadly-agreed description of each concept.
 *   • No new scholarly interpretations.
 *   • No invented Qur'anic / hadith references.
 *   • No madhhab-specific rulings.
 *   • No country-specific legal claims.
 * Every substantive claim is linked out to an existing entry in
 * `learningResources.js`, which already attributes to a named scholar
 * or institution (NZF, SeekersGuidance, AMJA, Qaradawi, etc.). The
 * calculators remain the single source of calculation truth.
 *
 * TWO LAYERS OF DISCLOSURE are visible on each card:
 *   1. A "General education" chip + a short line reminding users that
 *      scholarly / jurisdictional nuance applies.
 *   2. For Wasiyyah specifically, an extra chip calling out that civil
 *      inheritance law varies by country and warrants legal review.
 *
 * Any new fiqh text added here must be flagged for qualified scholarly
 * review in the change log. Country-specific legal claims must be
 * flagged for qualified legal review.
 */

// Pick a stable set of resource IDs from the existing library. Each
// corresponds to a known entry — if a future audit removes any ID,
// the card gracefully degrades (we filter nulls below).
const CARDS = [
  {
    id: 'zakat',
    icon: Calculator,
    kicker: 'Zakat',
    title: 'Understanding Assets and Nisab',
    summary:
      'Zakat is a yearly obligation on specific types of wealth once they exceed a minimum threshold called Nisab. The common bases are cash, savings, investments, business inventory and precious metals. Rates and threshold methodology differ slightly between schools and institutions — this app follows standards cited on each calculator page.',
    chips: ['General education', 'Verify with a qualified scholar'],
    resourceIds: ['z-001', 'z-002', 'z-004'],
    calc: { to: '/zakat/calculator', label: 'Open the Zakat calculator' },
    anchor: '/learn#zakat',
  },
  {
    id: 'faraid',
    icon: Scale,
    kicker: 'Faraid',
    title: 'Understanding Inheritance Shares',
    summary:
      'Islamic inheritance (Faraid) distributes a defined share of the net estate to each eligible heir. Shares depend on relationship (parents, spouse, children, siblings) and the surviving family composition. Classical rules such as awl (proportional reduction when shares exceed one) and radd (redistribution of a remainder) are built into the calculator and attributed on-screen.',
    chips: ['General education', 'School-specific nuance applies'],
    resourceIds: ['i-001', 'i-003', 'i-007'],
    calc: { to: '/inheritance', label: 'Open the Faraid calculator' },
    anchor: '/learn#inheritance',
  },
  {
    id: 'wasiyyah',
    icon: Scroll,
    kicker: 'Wasiyyah',
    title: 'Islamic Bequests and Local Legal Requirements',
    summary:
      'A Wasiyyah (Islamic will) typically covers up to one-third of the net estate for non-heir beneficiaries, the appointment of an executor and guardianship for minors. Civil probate, forced-heirship and witness requirements vary by country — the calculator surfaces jurisdiction notes but does not replace a qualified lawyer in your country.',
    chips: ['General education', 'Country law varies — consult a lawyer'],
    resourceIds: ['w-001', 'w-002', 'w-003'],
    calc: { to: '/will', label: 'Open the Wasiyyah writer' },
    anchor: '/learn#will',
  },
];

const resolveResource = (id) => RESOURCES.find((r) => r.id === id);

const ResourceLink = ({ resource }) => {
  if (!resource) return null;
  const src = SOURCES[resource.source];
  return (
    <a
      href={resource.url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`understand-link-${resource.id}`}
      className="group flex items-start gap-2 py-1.5 text-[12.5px] text-stone-700 hover:text-emerald-700 transition-colors"
    >
      <BookOpen className="w-3.5 h-3.5 mt-0.5 text-stone-400 group-hover:text-emerald-700 shrink-0" aria-hidden />
      <span className="min-w-0">
        <span className="truncate inline-block max-w-full align-top">{resource.title}</span>
        {src && (
          <span className="ml-1.5 inline-flex items-center text-[10.5px] font-mono uppercase tracking-wider text-stone-500 border border-stone-300 rounded px-1 py-[1px] align-middle">
            {src.short}
          </span>
        )}
      </span>
    </a>
  );
};

export const UnderstandBeforeYouCalculate = ({ testid = 'understand-before-calc' }) => (
  <section
    id="understand-before-calculate"
    data-testid={`section-${testid}`}
    aria-labelledby="understand-heading"
    className="page-section scroll-mt-[80px] py-10 md:py-14 border-b border-stone-200 bg-white"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Section heading — intentionally compact, matches existing section
          rhythm (eyebrow + H2 + concise lede). */}
      <div className="mb-6 md:mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.18em] text-gold-600 font-semibold mb-1.5">
            <Library className="w-3.5 h-3.5" aria-hidden /> Learn before you calculate
          </div>
          <h2 id="understand-heading" className="font-heading text-2xl md:text-3xl leading-tight text-[#0D1520]">
            Understand before you calculate.
          </h2>
          <p className="text-sm md:text-[15px] text-stone-600 mt-1.5 leading-relaxed max-w-2xl">
            A short plain-language primer on each pillar before you open the calculator. Every source listed below is from an existing scholar or institution — nothing here is a new ruling.
          </p>
        </div>
        <Link
          to="/learn"
          data-testid={`${testid}-visit-library`}
          className="group hidden md:inline-flex items-center gap-1.5 text-[13px] font-semibold text-emerald-700 hover:text-emerald-800 transition-colors"
        >
          Full Learning Library
          <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" aria-hidden />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5" data-testid={`${testid}-grid`}>
        {CARDS.map((card) => {
          const Icon = card.icon;
          const resources = card.resourceIds.map(resolveResource).filter(Boolean);
          return (
            <article
              key={card.id}
              data-testid={`understand-card-${card.id}`}
              className="flex flex-col rounded-2xl border border-stone-200 bg-white hover:border-emerald-700/40 hover:shadow-[0_8px_28px_-16px_rgba(13,71,51,0.35)] transition-all"
            >
              <div className="p-5 md:p-6 flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-emerald-700/8 text-emerald-700 ring-1 ring-emerald-700/15">
                    <Icon className="w-4.5 h-4.5" aria-hidden />
                  </span>
                  <span className="text-[10.5px] font-mono uppercase tracking-[0.18em] text-gold-600 font-semibold">
                    {card.kicker}
                  </span>
                </div>

                <h3 className="font-heading text-[18px] md:text-[19px] leading-tight text-[#0D1520] mb-2">
                  {card.title}
                </h3>
                <p className="text-[13.5px] text-stone-700 leading-relaxed mb-3">
                  {card.summary}
                </p>

                {/* Scope-of-content chips — unmistakeable boundary markers */}
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {card.chips.map((chip) => (
                    <span
                      key={chip}
                      className="inline-flex items-center gap-1 text-[10.5px] font-mono uppercase tracking-wider text-stone-600 border border-stone-300 rounded-full px-2 py-[2px] bg-[#FAF8F5]"
                    >
                      <Info className="w-2.5 h-2.5" aria-hidden /> {chip}
                    </span>
                  ))}
                </div>

                {/* Linked existing resources */}
                {resources.length > 0 && (
                  <div className="border-t border-stone-200 pt-3">
                    <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-stone-500 font-semibold mb-1.5">
                      Scholarly sources
                    </div>
                    <div className="flex flex-col -my-0.5">
                      {resources.map((r) => (
                        <ResourceLink key={r.id} resource={r} />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Card foot — library + calculator links */}
              <div className="px-5 md:px-6 py-3 border-t border-stone-200 bg-[#FAF8F5] rounded-b-2xl flex items-center justify-between gap-3 flex-wrap">
                <Link
                  to={card.anchor}
                  data-testid={`understand-${card.id}-more`}
                  className="inline-flex items-center gap-1 text-[12.5px] font-medium text-stone-700 hover:text-emerald-700 transition-colors"
                >
                  <ScrollText className="w-3.5 h-3.5" aria-hidden /> More in Learning Library
                </Link>
                <Link
                  to={card.calc.to}
                  data-testid={`understand-${card.id}-calc`}
                  className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-emerald-700 hover:text-emerald-800 transition-colors"
                >
                  {card.calc.label}
                  <ArrowRight className="w-3.5 h-3.5" aria-hidden />
                </Link>
              </div>
            </article>
          );
        })}
      </div>

      {/* Mobile "Full library" affordance (desktop version lives in the
          section header). */}
      <div className="md:hidden mt-5">
        <Link
          to="/learn"
          data-testid={`${testid}-visit-library-mobile`}
          className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-emerald-700"
        >
          Full Learning Library <ArrowRight className="w-4 h-4" aria-hidden />
        </Link>
      </div>
    </div>
  </section>
);

export default UnderstandBeforeYouCalculate;
